/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { B2B } from '@/components/blocks/B2B';
import { OneClick } from '@/components/blocks/OneClick';
import { productDetailsAddToCartErrorSX } from '@/components/blocks/ProductDetails/styles/addToCartError';
import { TYPES } from '@/data/constants/product';
import { useBundleDetailsTable } from '@/data/Content/BundleDetailsTable';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType, productIsA } from '@/utils/productIsA';
import { Stack, Typography } from '@mui/material';
import { FC, MouseEvent, useContext, useMemo, useState } from 'react';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';

type Props = {
	standalone?: boolean;
	isDrawer?: boolean;
};
// eslint-disable-next-line complexity
export const ProductDetailsAddToCart: FC<Props> = ({ standalone = false, isDrawer = true }) => {
	const {
		// AddToCart,
		addToCurrentOrder,
		SignIn,
	} = useLocalization('productDetail');
	const {
		selection,
		hasInventory,
		addToCart,
		product,
		loginStatus,
		error = {},
		isSkuListTableDisplayed,
		availability,
		isLoadingInventory,
	} = useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useBundleDetailsTable> &
		ReturnType<typeof useSkuListTable>;
	const isKitOrBundle = isKitOrBundleType[product?.type as string];
	const isBundle = productIsA(product, TYPES.bundle);
	const sku = !isKitOrBundle ? selection?.sku : product;
	const buyable = !isKitOrBundle ? selection?.buyable : true;
	const { prices } = getProductDisplayInfo(sku, product);
	const otherDisabled = (!isBundle && !hasInventory) || !buyable || !prices?.offer;
	const [errorMessage, setErrorMessage] = useState<string>('');

	const isSizeSelected = !!selection?.sku && selection?.buyable;

	const disabledButton = isSkuListTableDisplayed
		? product?.items?.length === 0
		: otherDisabled || !isSizeSelected;

	const { someWstock, initialAvailability } = useStock(availability);

	const dtId = useMemo(() => `product-add-to-cart${standalone ? '-standalone' : ''}`, [standalone]);
	const addToCardValidator = (event: MouseEvent<HTMLElement>) => {
		if (isBundle) {
			addToCart(event);
			return;
		}
		if (disabledButton) {
			setErrorMessage('Por favor, selecciona tu talla');
			return;
		}
		setErrorMessage('');
		addToCart(event);
	};

	return (
		<Stack mt={!isDrawer ? '' : '6px'} mb={!isDrawer ? 0 : '7px'}>
			{isDrawer ? (
				someWstock === false && !isLoadingInventory && (availability?.length || 0) > 0 ? (
					<Typography
						sx={{
							font: 'normal normal bold 15px/20px Open Sans',
							color: '#A32F00',
						}}
						component={'p'}
					>
						{'Producto temporalmente agotado'}
					</Typography>
				) : null
			) : null}
			{errorMessage ? (
				<Typography
					sx={{
						font: 'normal normal bold 15px/20px Open Sans',
						color: '#A32F00',
					}}
				>
					{errorMessage}
				</Typography>
			) : null}

			<Stack mt={!isDrawer ? 0 : '17px'} mb={!isDrawer ? 0 : '18px'}>
				<OneClick
					data-testid={dtId}
					id={dtId}
					onClick={addToCardValidator}
					variant="contained"
					disabled={!someWstock}
					isOutOfOtock={initialAvailability ? !someWstock : false}
					sx={{ backgroundColor: '#727272' }}
				>
					<B2B>{loginStatus ? addToCurrentOrder.t() : SignIn.t()}</B2B>
					<B2B is={false}>
						{initialAvailability.current
							? !someWstock
								? 'Agotado'
								: 'Agregar al carrito'
							: !someWstock
							? 'Agotado'
							: 'Cargando...'}
					</B2B>
				</OneClick>
				{/* <ProductDetailsAddToWishList standalone={standalone} /> */}
			</Stack>
			{error.message ? (
				<Typography variant="body2" sx={productDetailsAddToCartErrorSX}>
					{error.message}
				</Typography>
			) : null}
		</Stack>
	);
};
